import api from '@shared/services/api'

const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toISOString().split('T')[0];
};

export const positionsService = {
    createPosition: async (positionData) => {
        try {
            const formattedData = {
                hrJobTypeId: positionData.hrJobTypeId ? parseInt(positionData.hrJobTypeId) : null,
                hrJobDescriptionId: positionData.hrJobDescriptionId ? parseInt(positionData.hrJobDescriptionId) : null,
                workCategory: positionData.workCategory || null,
                degreeLevelId: positionData.degreeLevelId ? parseInt(positionData.degreeLevelId) : null,
                empBranchId: positionData.empBranchId ? parseInt(positionData.empBranchId) : null,
                jobCode: positionData.jobCode || ' ',
                shortDescription: positionData.shortDescription || ' ',
                longDescription: positionData.longDescription || ' ',
                openDate: formatDate(positionData.openDate),
                closeDate: formatDate(positionData.closeDate), // Don't override - let the form value be sent
                nkpd: positionData.nkpd || ' ',
                workTime: positionData.workTime ? parseInt(positionData.workTime) : null,
                minSalary: positionData.minSalary ? parseFloat(positionData.minSalary) : null,
                maxSalary: positionData.maxSalary ? parseFloat(positionData.maxSalary) : null,
                addPayedLeave: positionData.addPayedLeave ? parseInt(positionData.addPayedLeave) : null,
                minProfServiceYears: positionData.minProfServiceYears ? parseInt(positionData.minProfServiceYears) : null,
                leaderPosition: positionData.leaderPosition,
                minProfServiceMonths: positionData.minProfServiceMonths ? parseInt(positionData.minProfServiceMonths) : null,
                categoryOfStaff: positionData.categoryOfStaff || ' ',
                priority: positionData.priority ? parseInt(positionData.priority) : null
            };

            const response = await api.post('/hr-job', formattedData);
            return response.data;
        } catch (error) {
            console.error('Error creating position:', error);
            throw error;
        }
    },
    getAllPositions: async ({
        pageNumber = 0,
        pageSize = 10,
        sortBy = 'jobCode',
        sortDir = 'desc',
        jobCode,
        longDescription,
        ...searchParams
    }) => {
        try {
            const params = new URLSearchParams();

            // Add pagination and sorting params
            params.append('page', pageNumber);
            params.append('size', pageSize);
            params.append('sortBy', sortBy || 'jobCode');
            params.append('sortOrder', sortDir);

            // Add search parameters if they exist
            if (jobCode) {
                params.append('jobCode', jobCode.trim());
            }
            if (longDescription) {
                params.append('longDescription', longDescription.trim());
            }

            // Add any additional search parameters
            Object.entries(searchParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    if (typeof value === 'string' && value.trim()) {
                        params.append(key, value.trim());
                    }
                    else if (typeof value !== 'string') {
                        params.append(key, String(value));
                    }
                }
            });

            const queryString = params.toString();
            const url = `/hr-job${queryString ? `?${queryString}` : ''}`;
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching positions:', error);
            throw error;
        }
    },
    deletePosition: async (id) => {
        try {
            const response = await api.delete(`/hr-job/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting position:', error);
            throw error;
        }
    },
    updatePosition: async (id, positionData) => {
        try {
            const formattedData = {
                hrJobTypeId: positionData.hrJobTypeId ? parseInt(positionData.hrJobTypeId) : null,
                hrJobDescriptionId: positionData.hrJobDescriptionId ? parseInt(positionData.hrJobDescriptionId) : null,
                hrLaborCategoryId: 1,
                workCategory: positionData.workCategory || null,
                degreeLevelId: positionData.degreeLevelId ? parseInt(positionData.degreeLevelId) : null,
                empBranchId: positionData.empBranchId ? parseInt(positionData.empBranchId) : null,
                jobCode: positionData.jobCode || ' ',
                shortDescription: positionData.shortDescription || ' ',
                longDescription: positionData.longDescription || ' ',
                openDate: formatDate(positionData.openDate),
                closeDate: formatDate(positionData.closeDate), // Don't override - let the form value be sent
                nkpd: positionData.nkpd || ' ',
                workTime: positionData.workTime ? parseInt(positionData.workTime) : null,
                minSalary: positionData.minSalary ? parseFloat(positionData.minSalary) : null,
                maxSalary: positionData.maxSalary ? parseFloat(positionData.maxSalary) : null,
                addPayedLeave: positionData.addPayedLeave ? parseInt(positionData.addPayedLeave) : null,
                minProfServiceYears: positionData.minProfServiceYears ? parseInt(positionData.minProfServiceYears) : null,
                minProfServiceMonths: positionData.minProfServiceMonths ? parseInt(positionData.minProfServiceMonths) : null,
                leaderPosition: positionData.leaderPosition,
                categoryOfStaff: positionData.categoryOfStaff || ' ',
                priority: positionData.priority ? parseInt(positionData.priority) : null
            };

            const response = await api.put(`/hr-job/${id}`, formattedData);
            return response.data;
        } catch (error) {
            console.error('Error updating position:', error);
            throw error;
        }
    },
    saveTranslation: async (translationData) => {
        try {
            const formattedData = {
                jobId: translationData.jobId,
                langCode: "en_US",
                shortDescription: translationData.shortDescription,
                longDescription: translationData.longDescription
            };

            const response = await api.post('/hr-job/translate', formattedData);
            return response.data;
        } catch (error) {
            console.error('Error saving translation:', error);
            throw error;
        }
    },
    getTranslation: async (id) => {
        try {
            const response = await api.get(`/hr-job/translation/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching translation:', error);
            throw error;
        }
    },

    getJobRights: async () => {
        try {
            const response = await api.get('/hr-job/rights');
            return response.data;
        } catch (error) {
            console.error('Error fetching job rights:', error);
            throw error;
        }
    },

    getJobRightsById: async (jobId) => {
        try {
            const response = await api.get(`/hr-job/${jobId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching job rights by ID:', error);
            throw error;
        }
    },

    saveJobRights: async (jobId, rightsIds) => {
        try {
            const response = await api.put(`/hr-job/rights/${jobId}`, rightsIds);
            return response.data;
        } catch (error) {
            console.error('Error saving job rights:', error);
            throw error;
        }
    },
    getNomenclaturesByMnemonic: async (mnemonic, searchParams = {}) => {
        try {
            const params = {};
            if (searchParams && typeof searchParams === 'object') {
                Object.entries(searchParams).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        // Handle string values with trim
                        if (typeof value === 'string' && value.trim()) {
                            params[key] = value.trim();
                        }
                        // Handle non-string values (numbers, booleans, etc.)
                        else if (typeof value !== 'string') {
                            params[key] = value;
                        }
                    }
                });
            }
            const response = await api.get(`/nomenclatures/by-mnemonic/${mnemonic}`, { params });
            return response.data;
        } catch (error) {
            console.error("Error fetching nomenclatures by mnemonic:", error);
            throw error;
        }
    },

    getDegreeLevelList: async () => {
        try {
            const response = await api.get('/degree-levels/list');
            return response.data;
        } catch (error) {
            console.error('Error fetching degree level list:', error);
            throw error;
        }
    },

    getPositionType: async () => {
        try {
            const response = await api.get('/hr-job-type');
            return response.data;
        } catch (error) {
            console.error('Error fetching position type:', error);
            throw error;
        }
    },

    getPositionsFile: async (companyId) => {
        try {
            const response = await api.get(`/hr-job-description-dropdown/${companyId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching positions file:', error);
            throw error;
        }
    },

    getNkpdHierarchy: async (childValue) => {
        try {
            const response = await api.get(`/nkdp-hierarchy/${childValue}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching NKPD hierarchy:', error);
            throw error;
        }
    },

    getPositionsReportData: async (id) => {
        try {
            const response = await api.get(`/hr-job/${id}/report`);
            
            if (Array.isArray(response.data)) {
                return response.data;
            }
            
            return response.data.content || [];
        } catch (error) {
            console.error('Error fetching positions report data:', error);
            throw error;
        }
    }
};